import { useState, useRef } from 'react';
import { Dialog } from '@headlessui/react';
import ReCAPTCHA from 'react-google-recaptcha';
import { apiHelpers } from '../../services/api';
import config from '../../config';
import toast from 'react-hot-toast';
import {
  XMarkIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  CurrencyDollarIcon,
  LifebuoyIcon
} from '@heroicons/react/24/outline';

const mailboxConfig = {
  sales: {
    title: 'Contact Sales',
    icon: CurrencyDollarIcon,
    description: 'Have questions about our plans or need a custom solution? Send us a message and we\'ll get back to you shortly.',
    teamName: 'sales',
    responseTime: '24-48 hours'
  },
  support: {
    title: 'Contact Support',
    icon: LifebuoyIcon,
    description: 'Need help with your account or have a technical question? Our support team is here to help.',
    teamName: 'support',
    responseTime: '24 hours'
  }
};

export default function ContactSalesModal({ isOpen, onClose, mailboxType = 'sales' }) {
  const currentMailbox = mailboxConfig[mailboxType] || mailboxConfig.sales;
  const recaptchaRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [contactNumber, setContactNumber] = useState(null);
  const [captchaError, setCaptchaError] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCaptchaError(false);
    
    // Get reCAPTCHA token
    let recaptchaToken = null;
    if (config.recaptchaSiteKey && recaptchaRef.current) {
      recaptchaToken = recaptchaRef.current.getValue();
      if (!recaptchaToken) {
        setCaptchaError(true);
        toast.error('Please complete the CAPTCHA verification');
        return;
      }
    }
    
    setLoading(true);

    try {
      const response = await apiHelpers.submitMailboxContact(mailboxType, {
        ...formData,
        recaptchaToken
      });
      
      setContactNumber(response.data.contactNumber);
      setSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to send message. Please try again.';
      toast.error(errorMessage);
      
      // Reset reCAPTCHA on error
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset state when closing
    setSubmitted(false);
    setContactNumber(null);
    setCaptchaError(false);
    setFormData({
      name: '',
      email: '',
      subject: '',
      message: ''
    });
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
    onClose();
  };

  const handleCaptchaChange = (value) => {
    if (value) {
      setCaptchaError(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="card w-full max-w-lg max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <Dialog.Title className="text-lg font-semibold text-surface-900 dark:text-white flex items-center gap-2">
              <currentMailbox.icon className="w-6 h-6 text-primary-500" />
              {currentMailbox.title}
            </Dialog.Title>
            <button 
              onClick={handleClose} 
              className="text-surface-400 hover:text-surface-600 dark:hover:text-surface-300"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {submitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircleIcon className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-xl font-semibold text-surface-900 dark:text-white mb-2">
                Message Sent!
              </h3>
              <p className="text-surface-600 dark:text-surface-400 mb-4">
                Thank you for reaching out. Our {currentMailbox.teamName} team will get back to you within {currentMailbox.responseTime}.
              </p>
              {contactNumber && (
                <p className="text-sm text-surface-500 mb-6">
                  Reference: <span className="font-mono font-medium">{contactNumber}</span>
                </p>
              )}
              <p className="text-sm text-surface-500 mb-6">
                A confirmation email has been sent to <strong>{formData.email}</strong>
              </p>
              <button onClick={handleClose} className="btn-primary">
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="text-sm text-surface-600 dark:text-surface-400 mb-4">
                {currentMailbox.description}
              </p>
              
              <div>
                <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
                  Your Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="input"
                  placeholder="you@company.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
                  Subject *
                </label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="input"
                  placeholder="e.g., Question about Enterprise plan"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
                  Message *
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="input min-h-[120px]"
                  placeholder="Tell us about your needs, questions, or how we can help..."
                  required
                />
              </div>

              {/* reCAPTCHA */}
              {config.recaptchaSiteKey && (
                <div className="flex flex-col items-center">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={config.recaptchaSiteKey}
                    onChange={handleCaptchaChange}
                    theme="light"
                  />
                  {captchaError && (
                    <p className="text-sm text-red-500 mt-2 flex items-center gap-1">
                      <ExclamationTriangleIcon className="w-4 h-4" />
                      Please complete the CAPTCHA verification
                    </p>
                  )}
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button type="button" onClick={handleClose} className="btn-secondary flex-1">
                  Cancel
                </button>
                <button type="submit" disabled={loading} className="btn-primary flex-1">
                  {loading ? (
                    <>
                      <ArrowPathIcon className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <EnvelopeIcon className="w-4 h-4" />
                      Send Message
                    </>
                  )}
                </button>
              </div>

              <p className="text-xs text-center text-surface-500 mt-4">
                By submitting this form, you agree to our{' '}
                <a href="/#/privacy" className="text-primary-500 hover:underline">Privacy Policy</a>
              </p>
            </form>
          )}
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
